import React, { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  useWindowDimensions,
} from 'react-native';
import Svg, { Path, Circle, Line, Rect } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import { useTheme } from '../../../../../theme';

export type WorkspaceView = 'table' | 'map';

// Icons
const TableViewIcon = ({ color }: { color: string }) => (
  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <Rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2" fill="none" />
    <Line x1="3" y1="9" x2="21" y2="9" stroke={color} strokeWidth="2" />
    <Line x1="3" y1="15" x2="21" y2="15" stroke={color} strokeWidth="2" />
    <Line x1="9" y1="9" x2="9" y2="21" stroke={color} strokeWidth="2" />
  </Svg>
);

const MapViewIcon = ({ color }: { color: string }) => (
  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <Path
      d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <Path d="M8 2v16M16 6v16" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

const SearchIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <Circle cx="11" cy="11" r="8" stroke={AdminColors.textMuted} strokeWidth="2" />
    <Path d="M21 21l-4.35-4.35" stroke={AdminColors.textMuted} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

const CloseIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6l12 12" stroke={AdminColors.textSecondary} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

const FilterIcon = () => (
  <Svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <Path
      d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"
      stroke={AdminColors.textSecondary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Svg>
);

interface WorkspaceViewBarProps {
  activeView: WorkspaceView;
  onViewChange: (view: WorkspaceView) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchClear?: () => void;
  onMobileFilterPress?: () => void;
  filterSlot?: React.ReactNode;
  moreFiltersCount?: number;
  hideFilterSlot?: boolean;
}

const WorkspaceViewBar: React.FC<WorkspaceViewBarProps> = ({
  activeView,
  onViewChange,
  searchValue = '',
  onSearchChange,
  onSearchClear,
  onMobileFilterPress,
  filterSlot,
  moreFiltersCount = 0,
  hideFilterSlot = false,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isDark = useTheme().resolvedTheme === 'dark';
  const activeToggleColor = isDark ? '#FFFFFF' : AdminColors.blue800;
  const inactiveToggleColor = isDark ? '#B0B0B0' : AdminColors.textSecondary;
  // Dark theme: the track and the active tab otherwise map to the same dark grey,
  // so make the active tab a solid brand-blue pill on a black track.
  const darkSwitch = isDark ? { backgroundColor: '#000000', borderWidth: 1, borderColor: '#3A3A3A' } : null;
  const darkActive = isDark ? { backgroundColor: '#3D8BFF' } : null;

  return (
    <View style={styles.bar}>
      {/* View toggle: Table / Map */}
      <View style={[styles.viewSwitch, darkSwitch]}>
        <TouchableOpacity
          style={[styles.viewBtn, activeView === 'table' && styles.viewBtnActive, activeView === 'table' && darkActive]}
          onPress={() => onViewChange('table')}
          activeOpacity={0.7}
        >
          <View>
            <TableViewIcon color={activeView === 'table' ? activeToggleColor : inactiveToggleColor} />
          </View>
          <Text style={[styles.viewBtnTxt, activeView === 'table' && styles.viewBtnTxtActive]}>
            Table
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.viewBtn, activeView === 'map' && styles.viewBtnActive, activeView === 'map' && darkActive]}
          onPress={() => onViewChange('map')}
          activeOpacity={0.7}
        >
          <View>
            <MapViewIcon color={activeView === 'map' ? activeToggleColor : inactiveToggleColor} />
          </View>
          <Text style={[styles.viewBtnTxt, activeView === 'map' && styles.viewBtnTxtActive]}>
            Map
          </Text>
        </TouchableOpacity>
      </View>

      {/* Filter slot (primary filters rendered here on desktop) */}
      {!hideFilterSlot && !isMobile && filterSlot && (
        <View style={styles.filterSlot}>
          {filterSlot}
        </View>
      )}

      {/* Spacer */}
      <View style={{ flex: 1 }} />

      {/* Mobile: Filters button */}
      {!hideFilterSlot && isMobile && onMobileFilterPress && (
        <TouchableOpacity
          style={styles.mobileFilterBtn}
          onPress={onMobileFilterPress}
          activeOpacity={0.7}
        >
          <FilterIcon />
          <Text style={styles.mobileFilterTxt}>Filters</Text>
          {moreFiltersCount > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeTxt}>{moreFiltersCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      )}

      {/* Search input */}
      {onSearchChange && onSearchClear && (
        <View style={[styles.searchWrap, searchValue.length > 0 && styles.searchWrapActive]}>
          <SearchIcon />
          <TextInput
            style={styles.searchInput}
            value={searchValue}
            onChangeText={onSearchChange}
            placeholder="Search locations…"
            placeholderTextColor={AdminColors.gray350}
            returnKeyType="search"
          />
          {searchValue.length > 0 && (
            <TouchableOpacity onPress={onSearchClear} style={styles.clearBtn} activeOpacity={0.7}>
              <CloseIcon />
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    paddingHorizontal: 15,
    paddingVertical: 13,
    backgroundColor: AdminColors.bgSurface,
  },
  viewSwitch: {
    flexDirection: 'row',
    gap: 4,
    padding: 4,
    backgroundColor: '#E7EDF5',
    borderRadius: 8,
  },
  viewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    height: 32,
    paddingHorizontal: 13,
    borderRadius: 6,
    backgroundColor: 'transparent',
  },
  viewBtnActive: {
    backgroundColor: AdminColors.bgSurface,
    shadowColor: 'rgba(0,56,120,0.15)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 5,
    elevation: 2,
  },
  viewBtnTxt: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
  viewBtnTxtActive: {
    color: AdminColors.blue800,
  },
  filterSlot: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 7,
  },
  mobileFilterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 24,
    backgroundColor: AdminColors.bgSurface,
  },
  mobileFilterTxt: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  filterBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: AdminColors.blue700,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  filterBadgeTxt: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 11,
    minWidth: 220,
    flex: 1,
    maxWidth: 340,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 7,
    backgroundColor: AdminColors.bgSurface,
  },
  searchWrapActive: {
    borderColor: AdminColors.blue700,
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    height: 34,
    padding: 0,
  },
  clearBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: AdminColors.gray25,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default WorkspaceViewBar;
